import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowPathIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import ReportMonthSelect from '@/components/ReportMonthSelect'
import SearchableSelect from '@/components/SearchableSelect'
import WarehouseActionModal from '@/components/WarehouseActionModal'
import WarningBanner from '@/components/WarningBanner'
import { usePageAccess } from '@/hooks/usePageAccess'
import { HoverHelp } from '@/tableControls'
import { BARE_TRIGGER } from '@/pages/projections/gridClasses'
import { formatReadinessCheckedByTooltip } from '@/pages/distribution-waterfall/allocationMonthReadinessUtils'
import {
  deleteDraft,
  generateReport,
  getMonths,
  getReadiness,
  getReport,
  getReportHtml,
  getSettings,
  listProjectionCases,
  listReports,
  listSendsForMonth,
  previewCommentary,
  rewriteCommentary,
  saveCommentary,
  acknowledgeCheck,
  setReportFinal,
  setReportSent,
  sendReport,
  MonthlyPerformanceHttpError,
} from '@/services/monthlyPerformance'
import { getJobStatus, isBackgroundJobActive, type BackgroundJob } from '@/services/jobs'
import { useBackgroundJobsStore } from '@/store/backgroundJobsStore'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import type {
  MonthlyPerformanceCommentary,
  MonthlyPerformanceImportRef,
  MonthlyPerformanceProjectionCase,
  MonthlyPerformanceReadiness,
  MonthlyPerformanceReport as Report,
  MonthlyPerformanceReportSummary,
  MonthlyPerformanceSettings,
  MonthlyPerformanceSendLogEntry,
} from '@/types/monthlyPerformance'
import {
  cloneCommentary,
  commentaryEquals,
  defaultProjectionCaseId,
  defaultProjectionCaseMissingSnapshot,
  formatChicagoImportTimestamp,
  formatEmptyStateTitle,
  formatProjectionCaseOptionLabel,
  formatProjectionMetaLine,
  draftCommentaryButtonLabel,
  formatGenerateButtonState,
  formatLatestImportLine,
  formatNotImportableMessage,
  formatSendMembersButtonState,
  finalReport,
  historyRowLastActivity,
  formatDraftImportStaleBanner,
  formatImportMetaMutedLine,
  formatStaleMembersSendTooltip,
  formatYyyyMmDd,
  latestSentReport,
  GENERATE_REPORT_HELP,
  GENERATION_STEPS,
  mapUnverifiedSentences,
  monthsToSelectOptions,
  NONE_PROJECTION_CASE_ID,
  REFRESH_PREVIEW_HELP,
  renderEmailSubject,
  REWRITE_COMMENTARY_HELP,
} from './monthlyPerformanceUtils'
import ChecksCard from './ChecksCard'
import CommentaryTextList from './CommentaryTextList'
import MonthlyPerformanceSendModal from './MonthlyPerformanceSendModal'
import { useMonthlyPerformanceMonth } from './useMonthlyPerformanceMonth'

const COMMENTARY_BY_CUT_KEYS = ['total', 'river_oaks', 'highland_park', 'ga'] as const

const COMMENTARY_BY_CUT_LABELS: Record<(typeof COMMENTARY_BY_CUT_KEYS)[number], string> = {
  river_oaks: 'River Oaks',
  highland_park: 'Highland Park',
  ga: 'G&A',
  total: 'Consolidated',
}

function RenderingPreviewStatus({ job }: { job: BackgroundJob | null }) {
  if (!job || !isBackgroundJobActive(job.status)) return null
  return (
    <p className="inline-flex items-center gap-2 text-sm text-gray-700">
      <ArrowPathIcon className="h-4 w-4 animate-spin text-gray-500" aria-hidden />
      Rendering preview…
    </p>
  )
}

function wrapHoverHelp(label: ReactNode, help: string, ariaLabel: string) {
  return (
    <span className={BARE_TRIGGER}>
      <HoverHelp label={label} aria-label={ariaLabel}>
        {help}
      </HoverHelp>
    </span>
  )
}

const HISTORY_PILL_ACTIVE =
  'inline-flex rounded-full border border-emerald-600 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 transition-colors disabled:cursor-not-allowed disabled:opacity-50'
const HISTORY_PILL_INACTIVE =
  'inline-flex rounded-full border border-dashed border-gray-300 bg-transparent px-2.5 py-0.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'

function HistoryTogglePill({
  active,
  activeLabel,
  inactiveLabel,
  ariaLabel,
  disabled,
  onClick,
}: {
  active: boolean
  activeLabel: string
  inactiveLabel: string
  ariaLabel: string
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={active}
      onClick={onClick}
      className={active ? HISTORY_PILL_ACTIVE : HISTORY_PILL_INACTIVE}
    >
      {active ? activeLabel : inactiveLabel}
    </button>
  )
}

function emptyCommentary(): MonthlyPerformanceCommentary {
  return {
    headline: '',
    takeaways: [],
    watch_items: [],
    vs_projection: null,
    by_cut: { river_oaks: '', highland_park: '', ga: '', total: '' },
  }
}

function makePlaceholderJob(jobId: number, totalItems: number): BackgroundJob {
  return {
    id: jobId,
    job_type: 'monthly_performance',
    status: 'pending',
    total_items: totalItems,
    processed_items: 0,
    successful_items: 0,
    failed_items: 0,
    created_at: new Date().toISOString(),
  }
}

export default function MonthlyPerformanceReport() {
  const { canEdit } = usePageAccess('monthly-performance')
  const registerServerJob = useBackgroundJobsStore((s) => s.registerServerJob)
  const { months, setMonths, month, setMonth } = useMonthlyPerformanceMonth()
  const activeLoadMonthRef = useRef<string | null>(null)
  const activeLoadProjectionCasesRef = useRef<string | null>(null)
  const [readiness, setReadiness] = useState<MonthlyPerformanceReadiness | null>(null)
  const [report, setReport] = useState<Report | null>(null)
  const [history, setHistory] = useState<MonthlyPerformanceReportSummary[]>([])
  const [previewHtml, setPreviewHtml] = useState('')
  const [lastPreviewedCommentary, setLastPreviewedCommentary] =
    useState<MonthlyPerformanceCommentary | null>(null)
  const [previewRefreshing, setPreviewRefreshing] = useState(false)
  const [commentary, setCommentary] = useState<MonthlyPerformanceCommentary>(emptyCommentary())
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [rewriting, setRewriting] = useState(false)
  const [activeJob, setActiveJob] = useState<BackgroundJob | null>(null)
  const [showSendModal, setShowSendModal] = useState(false)
  const [showRewriteConfirm, setShowRewriteConfirm] = useState(false)
  const [pendingDeleteDraftId, setPendingDeleteDraftId] = useState<number | null>(null)
  const [pendingFinalChange, setPendingFinalChange] = useState<{
    reportId: number
    makeFinal: boolean
    otherFinalId?: number
  } | null>(null)
  const [pendingSentToggle, setPendingSentToggle] = useState<{
    reportId: number
    sent: boolean
    sendHint?: string
  } | null>(null)
  const [postSendPrompt, setPostSendPrompt] = useState<{
    reportId: number
    mode: 'test' | 'members'
  } | null>(null)
  const [reportHistoryOpen, setReportHistoryOpen] = useState(false)
  const [liveSettings, setLiveSettings] = useState<MonthlyPerformanceSettings | null>(null)
  const [readOnly, setReadOnly] = useState(false)
  const [persistedCommentary, setPersistedCommentary] =
    useState<MonthlyPerformanceCommentary | null>(null)
  const [commentarySaving, setCommentarySaving] = useState(false)
  const [latestImport, setLatestImport] = useState<MonthlyPerformanceImportRef | null>(null)
  const [sendLog, setSendLog] = useState<MonthlyPerformanceSendLogEntry[]>([])
  const [projectionCases, setProjectionCases] = useState<MonthlyPerformanceProjectionCase[]>([])
  const [projectionCaseId, setProjectionCaseId] = useState(NONE_PROJECTION_CASE_ID)

  const monthOptions = useMemo(() => monthsToSelectOptions(months), [months])
  const monthRow = useMemo(
    () => months.find((row) => row.month === month) ?? null,
    [month, months],
  )
  const generateState = formatGenerateButtonState(
    readiness ?? { can_generate: false, blocking_reason: null },
    { monthRow, importRef: latestImport },
  )
  const reportStale = report?.stale === true
  const sendMembersState = formatSendMembersButtonState(report)
  const finalReportSummary = useMemo(() => finalReport(history), [history])
  const commentarySourceId =
    finalReportSummary?.id ?? history[0]?.id ?? report?.id ?? 0
  const membersModalSendState = useMemo(() => {
    if (reportStale && report) {
      return {
        disabled: true,
        tooltip: formatStaleMembersSendTooltip({
          reportId: report.id,
          own: report.report_import,
          latest: report.import,
          commentarySourceId,
        }),
      }
    }
    return sendMembersState
  }, [commentarySourceId, report, reportStale, sendMembersState])
  const isLatestReport =
    report != null && history.length > 0 && report.id === history[0]!.id
  const canSendReport = isLatestReport || report?.is_final === true
  const unverifiedMatches = useMemo(
    () => mapUnverifiedSentences(commentary, report?.commentary_unverified),
    [commentary, report?.commentary_unverified],
  )
  const unverifiedPaths = useMemo(
    () => new Set(unverifiedMatches.map((m) => m.path)),
    [unverifiedMatches],
  )
  const previewOutOfSync = useMemo(
    () =>
      lastPreviewedCommentary !== null &&
      !commentaryEquals(commentary, lastPreviewedCommentary),
    [commentary, lastPreviewedCommentary],
  )

  const projectionCaseOptions = useMemo(() => {
    const reportCaseId = report?.projection_case_id
    const withSnapshot = projectionCases
      .filter((row) => row.has_snapshot)
      .map((row) => ({
        id: String(row.case_id),
        label: formatProjectionCaseOptionLabel(row.name, row.case_saved_at),
      }))
    const withoutSnapshot = projectionCases
      .filter((row) => !row.has_snapshot)
      .map((row) => ({
        id: String(row.case_id),
        label: row.name,
        disabled: row.case_id !== reportCaseId,
        hint: 'No projection recorded for this month',
      }))
    return [
      ...withSnapshot,
      ...withoutSnapshot,
      { id: NONE_PROJECTION_CASE_ID, label: 'None' },
    ]
  }, [projectionCases, report?.projection_case_id])

  const projectionDefaultMissingSnapshot =
    !report && defaultProjectionCaseMissingSnapshot(projectionCases)

  const settingsFromReport = useMemo(() => {
    const snapshot = report?.settings_snapshot as { settings?: MonthlyPerformanceSettings } | null
    return snapshot?.settings ?? null
  }, [report?.settings_snapshot])

  const emailDefaults = useMemo(() => {
    const base = liveSettings?.email ?? settingsFromReport?.email
    if (!base) return null
    return { ...base }
  }, [liveSettings?.email, settingsFromReport?.email])

  const periodLabel =
    typeof report?.dataset?.period_label === 'string'
      ? report.dataset.period_label
      : report
        ? renderEmailSubject(
            emailDefaults?.subject_template ?? 'TCG Monthly Financial Review - {month_label}',
            report.report_month,
          )
        : ''

  const sentReportForMonth = useMemo(() => latestSentReport(history), [history])
  const membersEmailSentMessage = useMemo(() => {
    if (!sentReportForMonth?.sent_at) return null
    return `Members report email sent ${formatChicagoImportTimestamp(sentReportForMonth.sent_at)}`
  }, [sentReportForMonth])

  useEffect(() => {
    void getSettings()
      .then(setLiveSettings)
      .catch(() => {
        // settings are optional for viewing; send modal falls back to snapshot
      })
  }, [])

  const loadMonthData = useCallback(async (targetMonth: string) => {
    activeLoadMonthRef.current = targetMonth
    setLoading(true)
    try {
      const [readinessData, reports, monthsData, sends] = await Promise.all([
        getReadiness(targetMonth),
        listReports(targetMonth),
        getMonths(),
        listSendsForMonth(targetMonth),
      ])
      if (activeLoadMonthRef.current !== targetMonth) return
      setReadiness(readinessData)
      if (readinessData.import) {
        setLatestImport(readinessData.import)
      }
      setHistory(reports)
      setSendLog(sends)
      setMonths(monthsData)
      const openSummary = finalReport(reports) ?? reports[0]
      if (openSummary) {
        const full = await getReport(openSummary.id)
        if (activeLoadMonthRef.current !== targetMonth) return
        setReport(full)
        if (full.import) {
          setLatestImport(full.import)
        }
        const latestDraftId = reports.find((r) => r.status === 'draft')?.id
        setReadOnly(
          full.status === 'sent' ||
            (full.id !== latestDraftId && !full.is_final),
        )
        const source = full.commentary_final ?? full.commentary_draft
        let loadedCommentary = emptyCommentary()
        if (source && typeof source === 'object' && 'headline' in source) {
          loadedCommentary = source as MonthlyPerformanceCommentary
        }
        setCommentary(loadedCommentary)
        setPersistedCommentary(cloneCommentary(loadedCommentary))
        const html = await getReportHtml(openSummary.id)
        if (activeLoadMonthRef.current !== targetMonth) return
        setPreviewHtml(html)
        setLastPreviewedCommentary(cloneCommentary(loadedCommentary))
      } else {
        setReport(null)
        setCommentary(emptyCommentary())
        setPersistedCommentary(null)
        setPreviewHtml('')
        setLastPreviewedCommentary(null)
        setReadOnly(false)
      }
    } catch (error) {
      if (activeLoadMonthRef.current !== targetMonth) return
      toast.error(formatApiError(error, 'Failed to load monthly performance data'))
    } finally {
      if (activeLoadMonthRef.current === targetMonth) {
        setLoading(false)
      }
    }
  }, [setMonths])

  useEffect(() => {
    if (!month) return
    setReport(null)
    setPreviewHtml('')
    setCommentary(emptyCommentary())
    setLastPreviewedCommentary(null)
    setPersistedCommentary(null)
    setReadOnly(false)
  }, [month])

  useEffect(() => {
    if (!month) return
    void loadMonthData(month)
    setReportHistoryOpen(false)
  }, [month, loadMonthData])

  useEffect(() => {
    if (!month) return
    activeLoadProjectionCasesRef.current = month
    void listProjectionCases(month)
      .then((cases) => {
        if (activeLoadProjectionCasesRef.current !== month) return
        setProjectionCases(cases)
      })
      .catch((error) => {
        if (activeLoadProjectionCasesRef.current !== month) return
        toast.error(formatApiError(error, 'Failed to load projection cases'))
      })
  }, [month])

  useEffect(() => {
    setProjectionCaseId(defaultProjectionCaseId(projectionCases, report?.projection_case_id))
  }, [month, projectionCases, report?.id, report?.projection_case_id])

  useEffect(() => {
    if (!activeJob) return
    let cancelled = false
    const poll = async () => {
      try {
        const job = await getJobStatus(activeJob.id)
        if (cancelled) return
        setActiveJob(job)
        registerServerJob(job)
        if (!isBackgroundJobActive(job.status)) {
          setGenerating(false)
          setActiveJob(null)
          if (job.status === 'completed' && month) {
            await loadMonthData(month)
          } else if (job.status === 'failed') {
            toast.error(job.error_message ?? 'Report generation failed')
          }
          return
        }
        window.setTimeout(() => void poll(), 1500)
      } catch (error) {
        if (!cancelled) {
          setGenerating(false)
          toast.error(formatApiError(error, 'Failed to poll generation job'))
        }
      }
    }
    void poll()
    return () => {
      cancelled = true
    }
  }, [activeJob, loadMonthData, month, registerServerJob])

  useEffect(() => {
    if (!report || readOnly || !canEdit || !persistedCommentary) return
    if (commentaryEquals(commentary, persistedCommentary)) return

    const timer = window.setTimeout(() => {
      void (async () => {
        setCommentarySaving(true)
        try {
          await saveCommentary(report.id, commentary)
          setPersistedCommentary(cloneCommentary(commentary))
          setReport((current) =>
            current
              ? { ...current, commentary_final: cloneCommentary(commentary) }
              : current,
          )
        } catch (error) {
          toast.error(formatApiError(error, 'Failed to save commentary'))
        } finally {
          setCommentarySaving(false)
        }
      })()
    }, 500)

    return () => window.clearTimeout(timer)
  }, [canEdit, commentary, persistedCommentary, readOnly, report])

  const handleGenerate = async () => {
    if (!month || !canEdit) return
    setGenerating(true)
    try {
      const { job_id, report_id } = await generateReport(
        month,
        projectionCaseId === NONE_PROJECTION_CASE_ID ? 'none' : Number(projectionCaseId),
      )
      const placeholder = makePlaceholderJob(job_id, GENERATION_STEPS.length)
      registerServerJob(placeholder)
      setActiveJob(placeholder)
      const full = await getReport(report_id)
      setReport(full)
      if (full.import) {
        setLatestImport(full.import)
      }
      setReadOnly(false)
      const source = full.commentary_final ?? full.commentary_draft
      let loadedCommentary = emptyCommentary()
      if (source && typeof source === 'object' && 'headline' in source) {
        loadedCommentary = source as MonthlyPerformanceCommentary
      }
      setCommentary(loadedCommentary)
      setPersistedCommentary(cloneCommentary(loadedCommentary))
      const html = await getReportHtml(report_id)
      setPreviewHtml(html)
      setLastPreviewedCommentary(cloneCommentary(loadedCommentary))
    } catch (error) {
      setGenerating(false)
      setActiveJob(null)
      toast.error(formatApiError(error, 'Failed to start generation'))
    }
  }

  const handleRefreshPreview = async () => {
    if (!report || readOnly || !canEdit || previewRefreshing) return
    const snapshot = cloneCommentary(commentary)
    setPreviewRefreshing(true)
    try {
      const { html } = await previewCommentary(report.id, commentary)
      setPreviewHtml(html)
      setLastPreviewedCommentary(snapshot)
      setPersistedCommentary(snapshot)
      setReport((current) =>
        current
          ? { ...current, commentary_final: snapshot, html }
          : current,
      )
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to refresh email preview'))
    } finally {
      setPreviewRefreshing(false)
    }
  }

  const runRewriteCommentary = async () => {
    if (!report || readOnly || !canEdit) return
    setRewriting(true)
    try {
      const result = await rewriteCommentary(report.id)
      if (result.commentary?.error) {
        setCommentary(emptyCommentary())
        toast.error(`Commentary generation failed: ${result.commentary.error}`)
      } else if (result.commentary_draft && 'headline' in result.commentary_draft) {
        const draft = result.commentary_draft
        setCommentary(draft)
        setPersistedCommentary(cloneCommentary(draft))
        setLastPreviewedCommentary(cloneCommentary(draft))
        toast.success('Commentary regenerated')
      }
      setPreviewHtml(result.html)
      if (month) await loadMonthData(month)
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to regenerate commentary'))
    } finally {
      setRewriting(false)
    }
  }

  const handleRegenerateCommentary = () => {
    if (!report || readOnly || !canEdit) return
    if (report.commentary_final) {
      setShowRewriteConfirm(true)
      return
    }
    void runRewriteCommentary()
  }

  const handleAcknowledgeCheck = async (key: string) => {
    if (!report || readOnly || !canEdit) return
    try {
      await acknowledgeCheck(report.id, key)
      if (month) await loadMonthData(month)
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to acknowledge check'))
    }
  }

  const handleSendFromModal = async (
    email: MonthlyPerformanceSettings['email'],
    mode: 'test' | 'members',
  ) => {
    if (!report || !canEdit) return
    try {
      const result = await sendReport(report.id, mode, {
        send_as: email.send_as,
        from_name: email.from_name,
        reply_to: email.reply_to,
        subject: email.subject_template,
        recipients: email.recipients,
        cc: email.cc,
        attach_comparison_pdf: email.attach_comparison_pdf ?? false,
      })
      setShowSendModal(false)
      const sentMode: 'test' | 'members' = result.mode === 'members' ? 'members' : 'test'
      if (sentMode === 'members') {
        setReportHistoryOpen(true)
      }
      if (month) await loadMonthData(month)
      if (report.is_final) {
        toast.success(sentMode === 'members' ? 'Report sent' : 'Test email sent')
      } else {
        setPostSendPrompt({ reportId: report.id, mode: sentMode })
      }
    } catch (error) {
      if (error instanceof MonthlyPerformanceHttpError && error.detail.error === 'send_blocked') {
        toast.error(String(error.detail.message ?? 'Send blocked'))
      } else {
        toast.error(formatApiError(error, 'Failed to send report'))
      }
    }
  }

  const handleDeleteDraft = (id: number) => {
    if (!canEdit) return
    setPendingDeleteDraftId(id)
  }

  const confirmDeleteDraft = async () => {
    if (pendingDeleteDraftId === null || !canEdit) return
    try {
      await deleteDraft(pendingDeleteDraftId)
      toast.success('Draft deleted')
      setPendingDeleteDraftId(null)
      if (month) await loadMonthData(month)
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to delete draft'))
    }
  }

  const handleViewReport = async (summary: MonthlyPerformanceReportSummary) => {
    try {
      const full = await getReport(summary.id)
      setReport(full)
      const latestDraftId = history.find((row) => row.status === 'draft')?.id
      setReadOnly(
        full.status === 'sent' ||
          (summary.id !== latestDraftId && !full.is_final),
      )
      const source = full.commentary_final ?? full.commentary_draft
      let loadedCommentary = commentary
      if (source && typeof source === 'object' && 'headline' in source) {
        loadedCommentary = source as MonthlyPerformanceCommentary
        setCommentary(loadedCommentary)
        setPersistedCommentary(cloneCommentary(loadedCommentary))
      }
      const html = await getReportHtml(summary.id)
      setPreviewHtml(html)
      setLastPreviewedCommentary(cloneCommentary(loadedCommentary))
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load report'))
    }
  }

  const latestDraftId = history.find((row) => row.status === 'draft')?.id
  const viewingEarlierDraft =
    readOnly &&
    report?.status === 'draft' &&
    !report.is_final
  const viewingEarlierDraftMessage = useMemo(() => {
    if (!viewingEarlierDraft || !report) return ''
    let message = `Viewing draft #${report.id} (read-only).`
    if (finalReportSummary) {
      message += ` Final: #${finalReportSummary.id}.`
    }
    if (latestDraftId != null) {
      message += ` Latest: #${latestDraftId}.`
    }
    return message
  }, [finalReportSummary, latestDraftId, report, viewingEarlierDraft])
  const importMismatch =
    report?.report_import != null &&
    report.import != null &&
    report.report_import.id !== report.import.id
  const showImportMutedLine =
    report != null &&
    report.import != null &&
    (report.status === 'sent' || report.is_final)
  const showImportStaleBanner =
    report != null &&
    importMismatch &&
    report.status !== 'sent' &&
    !report.is_final
  const historyToggleSuffix = finalReportSummary
    ? ` · Final #${finalReportSummary.id}`
    : ''

  const successfulSendForReport = (reportId: number) =>
    sendLog.find(
      (entry) =>
        entry.report_id === reportId &&
        !entry.error &&
        entry.gmail_message_id,
    )

  const requestFinalChange = (row: MonthlyPerformanceReportSummary, makeFinal: boolean) => {
    if (!canEdit) return
    if (makeFinal) {
      const other = history.find((item) => item.is_final && item.id !== row.id)
      if (other) {
        setPendingFinalChange({ reportId: row.id, makeFinal: true, otherFinalId: other.id })
        return
      }
    }
    setPendingFinalChange({ reportId: row.id, makeFinal })
  }

  const confirmFinalChange = async () => {
    if (pendingFinalChange === null || !canEdit || !month) return
    try {
      await setReportFinal(pendingFinalChange.reportId, pendingFinalChange.makeFinal)
      toast.success(pendingFinalChange.makeFinal ? 'Draft marked final' : 'Final mark removed')
      setPendingFinalChange(null)
      await loadMonthData(month)
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update final draft'))
    }
  }

  const confirmPostSendFinal = async () => {
    if (postSendPrompt === null || !canEdit || !month) return
    try {
      await setReportFinal(postSendPrompt.reportId, true)
      toast.success('Draft marked final')
      setPostSendPrompt(null)
      await loadMonthData(month)
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update final draft'))
    }
  }

  const requestSentToggle = (row: MonthlyPerformanceReportSummary, sent: boolean) => {
    if (!canEdit) return
    const send = successfulSendForReport(row.id)
    let sendHint: string | undefined
    if (sent && send) {
      sendHint = ` Uses the ${formatYyyyMmDd(send.sent_at)} email to ${(send.to ?? []).length} recipients.`
    }
    setPendingSentToggle({ reportId: row.id, sent, sendHint })
  }

  const confirmSentToggle = async () => {
    if (pendingSentToggle === null || !canEdit || !month) return
    try {
      await setReportSent(pendingSentToggle.reportId, pendingSentToggle.sent)
      toast.success(pendingSentToggle.sent ? 'Report marked as sent' : 'Report unmarked as sent')
      setPendingSentToggle(null)
      await loadMonthData(month)
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update sent status'))
    }
  }
  const previewDimmed = loading || previewRefreshing || generating

  const generateDisabled = !canEdit || generateState.disabled || generating
  const generateVariant = reportStale || !report ? 'primary' : 'outline'
  const generateHelp = generateDisabled ? generateState.tooltip : GENERATE_REPORT_HELP
  const generateButtonLabel = report ? 'Regenerate' : 'Generate report'
  const wrapGenerateButton = (label: string, helpId: string) =>
    wrapHoverHelp(
      <Button
        variant={generateVariant}
        disabled={generateDisabled}
        onClick={() => void handleGenerate()}
      >
        {label}
      </Button>,
      generateHelp,
      helpId,
    )

  const handleCopyHtml = async () => {
    if (previewOutOfSync) return
    try {
      await navigator.clipboard.writeText(previewHtml)
      toast.success('HTML copied')
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to copy HTML'))
    }
  }

  if (loading && !readiness) {
    return <p className="text-sm text-gray-500">Loading…</p>
  }

  const emptyStateTitle = month ? formatEmptyStateTitle(month) : ''
  const notImportableMessage =
    month && monthRow?.importable === false
      ? formatNotImportableMessage(month, latestImport?.last_closed_month)
      : null

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="mp-month">
                Report month
              </label>
              <ReportMonthSelect
                id="mp-month"
                options={monthOptions}
                value={month}
                onChange={setMonth}
              />
            </div>
            <div>
              {projectionDefaultMissingSnapshot
                ? wrapHoverHelp(
                    <label
                      className="mb-1 block text-sm font-medium text-gray-700"
                      htmlFor="mp-projection-case"
                    >
                      Compare to projection
                    </label>,
                    'The default case has no projection for this month',
                    'Default projection case warning',
                  )
                : (
                    <label
                      className="mb-1 block text-sm font-medium text-gray-700"
                      htmlFor="mp-projection-case"
                    >
                      Compare to projection
                    </label>
                  )}
              <SearchableSelect
                id="mp-projection-case"
                options={projectionCaseOptions}
                value={projectionCaseId}
                preserveOrder
                warning={projectionDefaultMissingSnapshot}
                onChange={(id) =>
                  setProjectionCaseId(id ?? NONE_PROJECTION_CASE_ID)
                }
              />
            </div>
          </div>
          {report ? (
            <p className="text-sm text-gray-600">
              {formatProjectionMetaLine(report.projection_source)}
            </p>
          ) : null}
          {report && showImportMutedLine ? (
            <p className="text-sm text-gray-500">
              {formatImportMetaMutedLine(report.report_import, report.import)}
            </p>
          ) : null}
          {membersEmailSentMessage ? (
            <p className="text-sm font-medium text-emerald-800" role="status">
              {membersEmailSentMessage}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!report ? wrapGenerateButton(generateButtonLabel, 'Generate report help') : null}
        </div>
      </div>

      <div className="shrink-0">
        <RenderingPreviewStatus job={activeJob} />
      </div>

      {!report && !loading ? (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900">{emptyStateTitle}</h2>
          {latestImport ? (
            <p className="mt-2 text-sm text-gray-600">{formatLatestImportLine(latestImport)}</p>
          ) : null}
          {notImportableMessage ? (
            <p className="mt-2 text-sm text-gray-700">{notImportableMessage}</p>
          ) : null}
          {readiness ? (
            <ul className="mt-4 space-y-2">
              {readiness.items.map((item) => {
                const notRequiredForReport = item.blocks_je_post === false
                const checkedMeta = formatReadinessCheckedByTooltip(item.checked_by, item.checked_at)
                return (
                  <li
                    key={item.id}
                    className={`flex flex-wrap items-start gap-2 text-sm ${
                      notRequiredForReport ? 'text-gray-500' : 'text-gray-900'
                    }`}
                  >
                    <span aria-hidden>{item.checked ? '✓' : '○'}</span>
                    <span className="min-w-0 flex-1">
                      <span>{item.label}</span>
                      {notRequiredForReport ? (
                        <span className="ml-1 text-gray-500">not required for this report</span>
                      ) : null}
                      {checkedMeta ? (
                        <span className="mt-0.5 block text-xs text-gray-500">{checkedMeta}</span>
                      ) : null}
                    </span>
                  </li>
                )
              })}
            </ul>
          ) : null}
          <p className="mt-4 text-sm">
            <Link
              className="text-brand-700 underline"
              to="/distribution-waterfall/calculator"
            >
              Open Income allocation
            </Link>
          </p>
        </section>
      ) : null}

      {report ? (
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          {showImportStaleBanner && report.import ? (
            <WarningBanner
              message={formatDraftImportStaleBanner({
                reportId: report.id,
                own: report.report_import,
                latest: report.import,
                commentarySourceId,
              })}
            />
          ) : null}
          {viewingEarlierDraft ? (
            <WarningBanner message={viewingEarlierDraftMessage} />
          ) : null}

          <ChecksCard
            checks={report.checks}
            acknowledgedChecks={report.acknowledged_checks}
            unverifiedSentences={report.commentary_unverified}
            onAcknowledge={(key) => void handleAcknowledgeCheck(key)}
            acknowledgeDisabled={readOnly || !canEdit}
          />

          <div className="grid min-h-0 min-w-0 flex-1 grid-cols-1 gap-6 lg:grid-cols-2">
          <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-5 py-3">
              <h2 className="container-title">
                Commentary
                {commentarySaving ? (
                  <span className="ml-2 text-sm font-normal text-gray-500">Saving…</span>
                ) : null}
              </h2>
              <div className="flex flex-wrap gap-2">
                {wrapHoverHelp(
                  <Button
                    variant="outline"
                    disabled={!canEdit || readOnly || generating || rewriting}
                    aria-busy={rewriting}
                    onClick={() => void handleRegenerateCommentary()}
                  >
                    {rewriting ? (
                      <span className="inline-flex items-center gap-2">
                        <ArrowPathIcon className="h-4 w-4 animate-spin text-gray-500" aria-hidden />
                        Drafting…
                      </span>
                    ) : (
                      draftCommentaryButtonLabel(report)
                    )}
                  </Button>,
                  readOnly || !canEdit ? 'Editing is disabled' : REWRITE_COMMENTARY_HELP,
                  'Draft commentary help',
                )}
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Headline</label>
                <textarea
                  className={`mt-1 w-full rounded border px-3 py-2 text-sm ${
                    unverifiedPaths.has('headline') ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
                  }`}
                  rows={2}
                  readOnly={readOnly}
                  value={commentary.headline}
                  title={
                    unverifiedMatches.find((m) => m.path === 'headline')?.reason ?? undefined
                  }
                  onChange={(e) => setCommentary({ ...commentary, headline: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Takeaways</label>
                <p className="text-xs text-gray-500">
                  One bullet each for Consolidated, River Oaks, Highland Park, and G&A. Add at most two
                  more only for cash, exceptional charges, or a trend not already covered.
                </p>
                <div className="mt-2 space-y-3">
                  {COMMENTARY_BY_CUT_KEYS.map((cut) => {
                    const path = `by_cut.${cut}`
                    return (
                      <div key={cut}>
                        <label className="text-sm font-normal text-gray-700">
                          {COMMENTARY_BY_CUT_LABELS[cut]}
                        </label>
                        <textarea
                          className={`mt-1 w-full rounded border px-3 py-2 text-sm ${
                            unverifiedPaths.has(path)
                              ? 'border-amber-300 bg-amber-50'
                              : 'border-gray-300'
                          }`}
                          rows={3}
                          readOnly={readOnly}
                          value={commentary.by_cut[cut]}
                          title={
                            unverifiedMatches.find((m) => m.path === path)?.reason ?? undefined
                          }
                          onChange={(e) =>
                            setCommentary({
                              ...commentary,
                              by_cut: { ...commentary.by_cut, [cut]: e.target.value },
                            })
                          }
                        />
                      </div>
                    )
                  })}
                </div>
                <div className="mt-3">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Other takeaways
                  </label>
                  <CommentaryTextList
                    items={commentary.takeaways}
                    pathPrefix="takeaways"
                    readOnly={readOnly}
                    resetKey={`${month ?? ''}-${report?.id ?? 'none'}`}
                    unverifiedPaths={unverifiedPaths}
                    unverifiedMatches={unverifiedMatches}
                    maxItems={Math.max(2, commentary.takeaways.length)}
                    addButtonLabel="Add takeaway"
                    generationError={report.commentary?.error}
                    onChange={(takeaways) => setCommentary({ ...commentary, takeaways })}
                  />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Watch items (0–3)</label>
                <CommentaryTextList
                  items={commentary.watch_items}
                  pathPrefix="watch_items"
                  readOnly={readOnly}
                  resetKey={`${month ?? ''}-${report?.id ?? 'none'}`}
                  unverifiedPaths={unverifiedPaths}
                  unverifiedMatches={unverifiedMatches}
                  maxItems={3}
                  addButtonLabel="Add watch item"
                  onChange={(watch_items) => setCommentary({ ...commentary, watch_items })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Versus projection</label>
                <p className="text-xs text-gray-500">Single paragraph, ≤ 60 words</p>
                <textarea
                  className={`mt-1 w-full rounded border px-3 py-2 text-sm ${
                    unverifiedPaths.has('vs_projection')
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-gray-300'
                  }`}
                  rows={3}
                  readOnly={readOnly}
                  value={commentary.vs_projection ?? ''}
                  title={
                    unverifiedMatches.find((m) => m.path === 'vs_projection')?.reason ?? undefined
                  }
                  onChange={(e) =>
                    setCommentary({
                      ...commentary,
                      vs_projection: e.target.value || null,
                    })
                  }
                />
              </div>
            </div>
            </div>
          </section>

          <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-5 py-3">
              <h2 className="container-title">Preview</h2>
              <div className="flex flex-wrap items-center gap-2">
                {report ? wrapGenerateButton(generateButtonLabel, 'Regenerate report help') : null}
                {!readOnly && previewOutOfSync
                  ? wrapHoverHelp(
                      <Button
                        variant="outline"
                        disabled={previewRefreshing}
                        onClick={() => void handleRefreshPreview()}
                      >
                        {previewRefreshing ? 'Refreshing…' : 'Refresh'}
                      </Button>,
                      REFRESH_PREVIEW_HELP,
                      'Refresh email preview help',
                    )
                  : null}
                <Button
                  variant="outline"
                  disabled={previewOutOfSync}
                  onClick={() => void handleCopyHtml()}
                >
                  Copy HTML
                </Button>
                {canSendReport ? (
                  <Button
                    variant={reportStale ? 'outline' : 'primary'}
                    disabled={!canEdit}
                    onClick={() => {
                      if (!emailDefaults) {
                        void getSettings()
                          .then((settings) => {
                            setLiveSettings(settings)
                            setShowSendModal(true)
                          })
                          .catch((error) => {
                            toast.error(formatApiError(error, 'Failed to load email settings'))
                          })
                        return
                      }
                      setShowSendModal(true)
                    }}
                  >
                    Send
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-5 py-4">
            <div className={`min-h-0 flex-1 ${previewDimmed ? 'opacity-50' : ''}`}>
              <iframe
                sandbox=""
                title="Preview"
                srcDoc={previewHtml}
                className="block h-full min-h-[480px] w-full border border-gray-200 bg-white lg:min-h-0"
              />
            </div>
            {history.length > 0 ? (
              <div className="mt-6 border-t border-gray-200 pt-4">
                <div className="flex items-center justify-start">
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    className="gap-1"
                    aria-expanded={reportHistoryOpen}
                    onClick={() => setReportHistoryOpen((open) => !open)}
                  >
                    {reportHistoryOpen
                      ? 'Hide history'
                      : `Report history (${history.length})${historyToggleSuffix}`}
                    <ChevronDownIcon
                      className={`h-4 w-4 shrink-0 transition-transform ${reportHistoryOpen ? '' : '-rotate-90'}`}
                      aria-hidden
                    />
                  </Button>
                </div>
                {reportHistoryOpen ? (
                  <table className="table-forecast mt-3 w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left">Draft</th>
                      <th className="text-left">Last Edited</th>
                      <th className="text-left">Projection</th>
                      <th className="text-left">Final</th>
                      <th className="text-left">Sent</th>
                      <th className="w-10" aria-hidden="true" />
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((row) => {
                      const lastActivity = historyRowLastActivity(row)
                      return (
                      <tr
                        key={row.id}
                        className={`cursor-pointer hover:bg-gray-50 ${
                          report?.id === row.id ? 'bg-sky-50 hover:bg-sky-50' : ''
                        }`}
                        onClick={() => void handleViewReport(row)}
                      >
                        <td>
                          <span>#{row.id}</span>
                        </td>
                        <td>
                          <div>{formatChicagoImportTimestamp(lastActivity.at)}</div>
                          <div className="text-xs text-gray-500">{lastActivity.by}</div>
                        </td>
                        <td>{row.projection_case_name ?? 'None'}</td>
                        <td onClick={(event) => event.stopPropagation()}>
                          <HistoryTogglePill
                            active={row.is_final}
                            activeLabel="Final"
                            inactiveLabel="Draft"
                            ariaLabel={`Final #${row.id}`}
                            disabled={!canEdit}
                            onClick={() =>
                              requestFinalChange(row, !row.is_final)
                            }
                          />
                        </td>
                        <td onClick={(event) => event.stopPropagation()}>
                          <div className="inline-flex flex-wrap items-center gap-2">
                            <HistoryTogglePill
                              active={row.status === 'sent'}
                              activeLabel="Sent"
                              inactiveLabel="Unsent"
                              ariaLabel={`Sent #${row.id}`}
                              disabled={!canEdit}
                              onClick={() =>
                                requestSentToggle(row, row.status !== 'sent')
                              }
                            />
                            <span className="text-gray-600">
                              {formatChicagoImportTimestamp(row.sent_at)}
                            </span>
                          </div>
                        </td>
                        <td
                          className="w-10 text-right"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {row.status !== 'sent' && !row.is_final ? (
                            <button
                              type="button"
                              className="inline-flex rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={!canEdit}
                              aria-label={`Delete draft #${row.id}`}
                              onClick={() => handleDeleteDraft(row.id)}
                            >
                              <XMarkIcon className="h-4 w-4" aria-hidden />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
                ) : null}
              </div>
            ) : null}
            </div>
            {emailDefaults && report ? (
              <MonthlyPerformanceSendModal
                open={showSendModal}
                periodLabel={periodLabel}
                baseEmail={emailDefaults}
                membersDisabled={membersModalSendState.disabled}
                membersDisabledReason={membersModalSendState.tooltip}
                sendLog={sendLog}
                onClose={() => setShowSendModal(false)}
                onSend={handleSendFromModal}
              />
            ) : null}
          </section>
          </div>
        </div>
      ) : null}

      {showRewriteConfirm ? (
        <WarehouseActionModal
          title="Re-draft commentary"
          submitLabel="Re-draft"
          onClose={() => setShowRewriteConfirm(false)}
          onSubmit={() => {
            setShowRewriteConfirm(false)
            void runRewriteCommentary()
          }}
        >
          <p className="text-sm text-gray-700">
            Replace your saved commentary edits with a new draft?
          </p>
        </WarehouseActionModal>
      ) : null}

      {pendingDeleteDraftId !== null ? (
        <WarehouseActionModal
          title="Delete draft"
          submitLabel="Delete"
          onClose={() => setPendingDeleteDraftId(null)}
          onSubmit={() => void confirmDeleteDraft()}
        >
          <p className="text-sm text-gray-700">Delete this draft report?</p>
        </WarehouseActionModal>
      ) : null}

      {pendingFinalChange ? (
        <WarehouseActionModal
          title={pendingFinalChange.makeFinal ? 'Mark final draft' : 'Unmark final draft'}
          submitLabel={pendingFinalChange.makeFinal ? 'Mark final' : 'Unmark'}
          onClose={() => setPendingFinalChange(null)}
          onSubmit={() => void confirmFinalChange()}
        >
          <p className="text-sm text-gray-700">
            {pendingFinalChange.makeFinal && pendingFinalChange.otherFinalId != null && month
              ? `Make #${pendingFinalChange.reportId} the final draft for ${periodLabel}? #${pendingFinalChange.otherFinalId} will no longer be final.`
              : pendingFinalChange.makeFinal
                ? `Mark #${pendingFinalChange.reportId} as the final draft for this month?`
                : `Remove the final mark from #${pendingFinalChange.reportId}?`}
          </p>
        </WarehouseActionModal>
      ) : null}

      {postSendPrompt ? (
        <WarehouseActionModal
          title="Email sent"
          submitLabel="Mark as Final"
          closeLabel="Not now"
          onClose={() => setPostSendPrompt(null)}
          onSubmit={() => void confirmPostSendFinal()}
        >
          <p className="text-sm text-gray-700">
            {postSendPrompt.mode === 'members'
              ? 'The report was emailed. This draft is marked Sent in history. Mark it as Final for this month?'
              : 'The test email was sent. This draft was not marked Sent. Mark it as Final for this month?'}
          </p>
        </WarehouseActionModal>
      ) : null}

      {pendingSentToggle ? (
        <WarehouseActionModal
          title={pendingSentToggle.sent ? 'Mark as sent' : 'Unmark as sent'}
          submitLabel={pendingSentToggle.sent ? 'Mark sent' : 'Unmark'}
          onClose={() => setPendingSentToggle(null)}
          onSubmit={() => void confirmSentToggle()}
        >
          <p className="text-sm text-gray-700">
            {pendingSentToggle.sent
              ? `Mark #${pendingSentToggle.reportId} as sent to members? It becomes read-only.${pendingSentToggle.sendHint ?? ''}`
              : `Unmark #${pendingSentToggle.reportId} as sent? It becomes editable again.`}
          </p>
        </WarehouseActionModal>
      ) : null}
    </div>
  )
}
